import React, { useState } from 'react';
import {
  FiMapPin, FiDroplet, FiUsers, FiCamera, FiMusic, FiBook, FiCoffee,
  FiSun, FiStar, FiHeart, FiFlag, FiHome, FiShoppingBag, FiGift
} from 'react-icons/fi';
import {
  LuTrees, LuLandmark, LuUtensils, LuMountain, LuBuilding, LuWaves, LuTrophy,
  LuPalette, LuMoon, LuAnchor, LuWine, LuBike, LuBird, LuTent, LuCompass,
  LuFootprints, LuLeaf, LuSnowflake, LuFish, LuDog, LuIceCream, LuChurch,
  LuCastle, LuTrain, LuPlane, LuShip, LuCar, LuUmbrella
} from 'react-icons/lu';

// Map of icon names to React components
const iconMap = {
  'map-pin': FiMapPin,
  'trees': LuTrees,
  'landmark': LuLandmark,
  'utensils': LuUtensils,
  'mountain': LuMountain,
  'building': LuBuilding,
  'waves': LuWaves,
  'trophy': LuTrophy,
  'users': FiUsers,
  'palette': LuPalette,
  'moon': LuMoon,
  'camera': FiCamera,
  'anchor': LuAnchor,
  'wine': LuWine,
  'bike': LuBike,
  'bird': LuBird,
  'tent': LuTent,
  'compass': LuCompass,
  'footprints': LuFootprints,
  'leaf': LuLeaf,
  'sun': FiSun,
  'snowflake': LuSnowflake,
  'fish': LuFish,
  'dog': LuDog,
  'music': FiMusic,
  'book': FiBook,
  'coffee': FiCoffee,
  'ice-cream': LuIceCream,
  'church': LuChurch,
  'castle': LuCastle,
  'train': LuTrain,
  'plane': LuPlane,
  'ship': LuShip,
  'car': LuCar,
  'umbrella': LuUmbrella,
  'star': FiStar,
  'heart': FiHeart,
  'flag': FiFlag,
  'home': FiHome,
  'shopping-bag': FiShoppingBag,
  'gift': FiGift,
  'droplet': FiDroplet
};

// List of all available icons in display order
const iconList = [
  'map-pin', 'trees', 'landmark', 'utensils', 'mountain', 'building',
  'waves', 'trophy', 'users', 'palette', 'moon', 'camera', 'anchor',
  'wine', 'bike', 'bird', 'tent', 'compass', 'footprints', 'leaf',
  'sun', 'snowflake', 'fish', 'dog', 'music', 'book', 'coffee',
  'ice-cream', 'church', 'castle', 'train', 'plane', 'ship', 'car',
  'umbrella', 'star', 'heart', 'flag', 'home', 'shopping-bag', 'gift'
];

/**
 * Render a Lucide icon by name
 * @param {string} iconName - The icon name (e.g., 'map-pin', 'trees')
 * @param {object} props - Additional props to pass to the icon component
 */
export const LucideIcon = ({ name, size = 20, color, className = '', ...props }) => {
  const IconComponent = iconMap[name];
  if (!IconComponent) {
    // Fallback to map-pin if icon not found
    const FallbackIcon = iconMap['map-pin'];
    return <FallbackIcon size={size} color={color} className={className} {...props} />;
  }
  return <IconComponent size={size} color={color} className={className} {...props} />;
};

/**
 * Icon Picker Component - Grid of selectable icons
 */
const IconPicker = ({
  selectedIcon,
  onSelect,
  color = '#6B7280',
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSize = {
    sm: 16,
    md: 20,
    lg: 24
  };

  const handleSelect = (iconName) => {
    onSelect(iconName);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Selected Icon Display / Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${sizeClasses[size]} rounded-lg border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500`}
        style={{ backgroundColor: color + '20' }}
      >
        <LucideIcon name={selectedIcon || 'map-pin'} size={iconSize[size]} color={color} />
      </button>

      {/* Dropdown Grid */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Icon Grid */}
          <div className="absolute z-50 mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 min-w-[280px] max-h-[320px] overflow-y-auto">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Select an icon</div>
            <div className="grid grid-cols-7 gap-1">
              {iconList.map((iconName) => (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => handleSelect(iconName)}
                  className={`w-8 h-8 rounded flex items-center justify-center transition-all hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    selectedIcon === iconName
                      ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500'
                      : ''
                  }`}
                  title={iconName}
                >
                  <LucideIcon name={iconName} size={18} color={selectedIcon === iconName ? color : '#6B7280'} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IconPicker;
export { iconMap, iconList };
